import {
  Box,
  Button,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Input,
  Select,
  Spinner,
  Text,
  VStack,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { createItem } from "helpers/utils";
import { useEffect, useMemo, useState } from "react";
import { Company, Inventory, Transfer, TransferInput, WithId } from "types";
import { useEntities } from "./hooks/useEntities";
import useUser from "./hooks/useUser";
import AutoSuggestInput from "./AutoSuggestInput";

const REQUIRED_FIELDS: Array<keyof TransferInput> = [
  "to_company_id",
  "item_id",
  "quantity",
];

interface TransferFormProps {
  onSave?: (transfer: WithId<Transfer>) => void;
  onClose?: () => void;
}

export default function TransferForm({ onSave, onClose }: TransferFormProps) {
  const { onOpen, ...disclosure } = useDisclosure();
  const toast = useToast();
  const [transfer, setTransfer] = useState<Partial<TransferInput>>({});
  const [balance, setBalance] = useState<number>(0);
  const { list: companies } = useEntities<WithId<Company>>({
    entity: "Company",
    pageSize: 100,
  });

  const { company_id } = useUser();

  const handleChange = (name: keyof TransferInput) => (e: any) => {
    const value = e?.target?.value ?? e?.value ?? e;
    const numberTypes = ["quantity"];
    const val = numberTypes.includes(name) ? parseInt(value ?? 0) : value;
    setTransfer((prev) => ({ ...prev, [name]: val }));
  };

  const isValid = () => {
    const missing = REQUIRED_FIELDS.filter((field) => !transfer[field]);
    return missing.length === 0;
  };

  const handleSubmit = async () => {
    if (!isValid()) {
      toast({
        title: "Error",
        description: "Please fill out all required fields",
        status: "error",
      });
      return;
    }

    if ((transfer.quantity ?? 0) > balance) {
      toast({
        title: "Error",
        description: "Quantity exceeds available balance",
        status: "error",
      });
      return;
    }

    try {
      const res = await createItem<Partial<TransferInput>>(
        "Transfer",
        {
          ...transfer,
          from_company_id: company_id,
          approval_status: "pending",
        },
        {
          fields: [
            "*",
            "requested_by.first_name",
            "requested_by.last_name",
            "item_id.*",
            "from_company_id.*",
            "to_company_id.*",
          ],
        }
      );

      toast({
        title: "Success",
        description: "Transfer initiated successfully",
        status: "success",
      });
      onSave?.(res as unknown as WithId<Transfer>);
      disclosure.onClose();
      handleClose();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error?.message ?? "Failed to create transfer",
        status: "error",
      });
    }
  };

  const handleClose = () => {
    setTransfer({});
    onClose?.();
  };

  return (
    <>
      <Button onClick={onOpen} colorScheme="blue" variant="outline" size={"sm"}>
        New Transfer
      </Button>
      <Drawer
        {...disclosure}
        placement="right"
        size={"sm"}
        onCloseComplete={handleClose}
      >
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>Initiate New Transfer</DrawerHeader>
          <Divider />
          <DrawerBody>
            <VStack alignItems={"start"} py={2} spacing={4}>
              <Box w={"100%"}>
                <Text size={"sm"} mb={2}>
                  To Company
                </Text>
                <Select
                  placeholder="-- Select Company --"
                  value={transfer.to_company_id ?? ""}
                  onChange={handleChange("to_company_id")}
                >
                  {companies
                    .filter((c) => c.id !== company_id)
                    .map((company) => (
                      <option key={company.id} value={company.id}>
                        {company.name}
                      </option>
                    ))}
                </Select>
              </Box>
              <Box w={"100%"}>
                <Text size={"sm"} mb={2}>
                  Item
                </Text>
                <AutoSuggestInput
                  entity="Item"
                  entityLabel="name"
                  entityValue="id"
                  filter={{ inventory_items: { id: { _nnull: true } } }}
                  deep={{
                    inventory_items: { _limit: 1, _sort: "-created_at" },
                  }}
                  value={transfer.item_id}
                  onChange={handleChange("item_id")}
                />
                <Text fontSize={"xs"} color={"gray.500"}>
                  Only items with a balance will be suggested
                </Text>
              </Box>
              {transfer.item_id && (
                <Quantity
                  value={transfer.quantity ?? ""}
                  onChange={handleChange("quantity")}
                  item_id={transfer.item_id}
                  onChangeBalance={setBalance}
                />
              )}
            </VStack>
          </DrawerBody>
          <DrawerFooter>
            <Button variant="outline" mr={3} onClick={disclosure.onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleSubmit}
              isDisabled={!isValid()}
            >
              Submit
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  );
}

interface QuantityProps {
  item_id: string;
  value: number | string;
  onChange: any;
  onChangeBalance?: (bal: number) => void;
}

const QTYSORT = ["-created_at"];

const Quantity = ({
  item_id,
  value,
  onChange,
  onChangeBalance,
}: QuantityProps) => {
  const filters = useMemo(() => ({ item_id: { _eq: item_id } }), [item_id]);

  const {
    list: [lastTx],
    isLoading,
  } = useEntities<Inventory>({
    entity: "Inventory",
    filter: filters,
    sort: QTYSORT,
    pageSize: 1,
    paginate: false,
  });

  useEffect(() => {
    onChangeBalance?.(lastTx?.balance ?? 0);
  }, [lastTx, onChangeBalance]);

  if (isLoading) return <Spinner />;

  return (
    <Box w={"100%"}>
      <Text size={"sm"} mb={2}>
        Quantity
      </Text>
      <Input
        type="number"
        placeholder="Enter quantity"
        value={value ?? ""}
        onChange={onChange}
        isDisabled={!lastTx || lastTx.balance < 1}
      />
      <Text fontSize={"xs"} color={"gray.500"}>
        {lastTx?.balance ?? 0} units available
      </Text>
    </Box>
  );
};
